import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import NeuralField, { BANDS, DIAL_TOP_Y, bandOfHz, colourForHz, flowScore, positionOfHz } from "./NeuralField";
import { Container, Lead, PrimaryButton, Arrow } from "./ui";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";
import { hero, links, award } from "../../pages/landingContent";

const Wrap = styled.section`
  position: relative;
  min-height: 100vh;
  min-height: 100svh;
  /* Same top offset as the vertical pages, so the eyebrow lands where their crumb does. */
  padding: 120px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background:
    radial-gradient(60% 50% at 85% 10%, rgba(216, 168, 137, 0.28), transparent 70%),
    radial-gradient(50% 40% at 5% 30%, rgba(163, 177, 143, 0.25), transparent 70%);
`;

// Columns match the vertical pages (title left, a picture-width column right), top-aligned
// so both sides start on one line.
const Copy = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 28px;

  @media ${media.large} {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
    align-items: start;
    gap: 72px;
  }
`;

const Title = styled.h1`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(48px, 7vw, 104px);
  line-height: 0.94;
  letter-spacing: -0.045em;
  color: ${colors.ink};
  /* No eyebrow above: the title starts level with the paragraph opposite. */
  margin-top: -0.08em;

  .line {
    display: block;
    overflow: hidden;
    padding-bottom: 0.16em;
    margin-bottom: -0.1em;
  }
  .line > span {
    display: inline-block;
  }
  em {
    font-style: normal;
    font-weight: 300;
    color: ${colors.copper};
  }
`;

const Side = styled.div`
  display: grid;
  gap: 18px;

  .close {
    display: block;
    margin-top: 8px;
    color: ${colors.ink};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

// The US recognition, given weight: a dark card against the light hero, display type.
const Award = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-top: 6px;
  padding: 14px 24px 14px 14px;
  border-radius: 999px;
  background: ${colors.forest};
  border: 1px solid rgba(209, 154, 102, 0.45);
  box-shadow: 0 24px 48px -32px rgba(30, 42, 34, 0.7);
  transition: border-color 0.25s ease, transform 0.25s ease;

  &:hover {
    border-color: ${colors.copperLight};
    transform: translateY(-1px);
  }

  svg {
    flex: none;
  }
  span {
    display: grid;
    gap: 3px;
    line-height: 1.25;
  }
  b {
    font-family: ${fonts.display};
    font-size: clamp(15px, 1.2vw, 17px);
    font-weight: 400;
    white-space: nowrap;
    letter-spacing: -0.01em;
    color: ${colors.linen};

    /* phones: the award line wraps inside the pill instead of widening the page */
    @media (max-width: 659px) {
      white-space: normal;
    }
  }
  b i {
    font-style: normal;
    color: ${colors.copperLight};
  }
  small {
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${colors.sage};
  }
`;

const Laurel = ({ size = 46 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
    <circle cx="20" cy="20" r="20" fill={colors.copper} />
    <circle cx="20" cy="20" r="18" fill="none" stroke={colors.copperLight} strokeOpacity="0.7" strokeWidth="1" />
    <path d="M13 27c-3-3-4-8-2-12M27 27c3-3 4-8 2-12" stroke={colors.copperLight} strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <path d="M11.5 18.5l-2.5-1M12 22.5l-2.8.2M13.8 25.6l-2.4 1.4M28.5 18.5l2.5-1M28 22.5l2.8.2M26.2 25.6l2.4 1.4" stroke={colors.sage} strokeWidth="1.5" strokeLinecap="round" />
    <text x="20" y="24" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontSize="12" fill={colors.linen}>30</text>
  </svg>
);

// The award card on its own, for other pages (the new home page's problem section).
export const AwardBadge = ({ className }) => (
  <Award className={className} href={award.href} target="_blank" rel="noopener noreferrer">
    <Laurel />
    <span>
      <b>
        {award.title} <i>·</i> {award.region} <i>·</i> {award.country}
      </b>
      <small>
        {award.year} · {award.source} · {award.origin}
      </small>
    </span>
  </Award>
);

const Field = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`;

// Share of the width the 40 → 1 Hz scale spans (20 Hz at the centre); the dial and ruler share it.
const DIAL_RANGE = [0.08, 0.92];
const posFor = (hz) => (DIAL_RANGE[0] + (DIAL_RANGE[1] - DIAL_RANGE[0]) * positionOfHz(hz)) * 100;
const rgb = (c) => `rgb(${c.join(",")})`;
// The oval behind the current state is a blurred pseudo-element (see the `glow` rule) so the
// colour fades from the middle out with no edge. The tone is handed over as a CSS variable.
const pill = (tone) => ({ "--tone": tone.replace("rgb(", "rgba(").replace(")", ",0.6)") });
const glow = `
  position: relative;
  z-index: 0;
  /* A radial fade rather than a blur filter: Safari leaves grey boxes behind blurred layers. */
  &::before {
    content: "";
    position: absolute;
    inset: -8px -18px;
    z-index: -1;
    border-radius: 999px;
    background: radial-gradient(ellipse closest-side, var(--tone, transparent) 0%, var(--tone, transparent) 45%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  &.on::before {
    opacity: 1;
  }
`;

// One brain state per band. Segments run to the page edges; `mid` (label centre) stays
// within the dial's 40 → 1 Hz travel.
const ZONES = [
  { band: BANDS[0], from: 0, to: posFor(30), mid: (posFor(40) + posFor(30)) / 2 },
  { band: BANDS[1], from: posFor(30), to: posFor(13) },
  { band: BANDS[2], from: posFor(13), to: posFor(8) },
  { band: BANDS[3], from: posFor(8), to: posFor(4) },
  { band: BANDS[4], from: posFor(4), to: 100, mid: (posFor(4) + posFor(1)) / 2 },
].map((z, i) => ({ mid: (z.from + z.to) / 2, ...z, state: hero.states[i] }));
const FLOW_ZONE = ZONES[3];
const FLOW_COLOUR = `#${BANDS[3].light.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
// Scale ends, centre, and the points where the brain state changes.
const TICKS = [40, 30, 20, 13, 8, 4, 1];

const Waves = styled.div`
  position: relative;
  flex: 1;
  min-height: 130px;
  cursor: crosshair;
`;

const FieldFade = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(to right, ${colors.limestone} 0%, transparent 6%, transparent 94%, ${colors.limestone} 100%);
`;

// State words sit just above the top trace (DIAL_TOP_Y), clear of the gamma wave.
const StateWords = styled.div`
  position: absolute;
  inset: 0 0 auto;
  height: ${DIAL_TOP_Y - 20}px;
  z-index: 1;
  pointer-events: none;

  /* Every word is centred on its zone, directly above the band name under the ruler. */
  span {
    position: absolute;
    bottom: 0;
    transform: translateX(-50%);
    padding: 5px 14px;
    border-radius: 999px;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${colors.pebble};
    opacity: 0.6;
    transition: color 0.3s ease, opacity 0.3s ease;
    ${glow}
    position: absolute;
  }
  /* The current state: a soft oval of its own colour behind light text. */
  span.on {
    opacity: 1;
    font-weight: 600;
    color: ${colors.linen};
  }
  /* Flow state is the goal: always bright. */
  span.flow:not(.on) {
    color: ${FLOW_COLOUR};
    opacity: 1;
    font-weight: 500;
    text-shadow: 0 0 12px ${FLOW_COLOUR}66;
  }

  @media (max-width: 1099px) {
    span {
      font-size: 10px;
    }
    span.mid:not(.on):not(.flow) {
      opacity: 0;
    }
  }
  /* Phones: the current state (plus flow when there is room), so words never collide. */
  @media (max-width: 659px) {
    span:not(.on):not(.flow) {
      opacity: 0;
    }
    ${(p) => (p.$crowded ? "span.flow:not(.on) { opacity: 0; }" : "")}
  }
`;

// Guide from the "Flow state" word down to the ruler.
const FlowGuide = styled.span`
  position: absolute;
  top: ${DIAL_TOP_Y - 16}px;
  bottom: -9px;
  width: 1px;
  margin-left: -0.5px;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(to bottom, ${FLOW_COLOUR}, ${FLOW_COLOUR}99);
  opacity: ${(p) => (p.$on ? 1 : 0.65)};
  box-shadow: ${(p) => (p.$on ? `0 0 12px ${FLOW_COLOUR}cc` : `0 0 6px ${FLOW_COLOUR}77`)};
  transition: opacity 0.4s ease, box-shadow 0.4s ease;
`;

// Frequency scale under the waves, aligned with the dial.
const Ruler = styled.div`
  position: relative;
  height: 48px;
  flex-shrink: 0;

  .seg {
    position: absolute;
    top: 9px;
    height: 3px;
    border-radius: 3px;
    background: ${colors.sand};
    transition: background 0.3s ease;
  }
  .tick {
    position: absolute;
    top: 5px;
    transform: translateX(-50%);
    display: grid;
    justify-items: center;
    gap: 3px;
    font-family: ${fonts.mono};
    font-size: 10px;
    color: ${colors.pebble};
    white-space: nowrap;
  }
  .tick::before {
    content: "";
    width: 1px;
    height: 11px;
    background: ${colors.stone};
  }
  .name {
    position: absolute;
    top: 30px;
    transform: translateX(-50%);
    padding: 4px 10px;
    border-radius: 999px;
    font-family: ${fonts.mono};
    font-size: 9px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${colors.pebble};
    opacity: 0.5;
    transition: color 0.3s ease, opacity 0.3s ease;
    ${glow}
    position: absolute;
  }
  .name.on {
    opacity: 1;
    font-weight: 600;
    color: ${colors.linen};
  }
  .tick.on {
    color: ${colors.ink};
    font-weight: 600;
  }
  .tick.on::before {
    background: currentColor;
  }
  .name .sym {
    display: none;
  }
  .marker {
    position: absolute;
    top: 5px;
    width: 11px;
    height: 11px;
    margin-left: -5.5px;
    border-radius: 50%;
    border: 2px solid ${colors.linen};
    transition: left 0.12s linear, background 0.3s ease;
  }

  @media (max-width: 659px) {
    .tick .unit,
    .name .full {
      display: none;
    }
    .name .sym {
      display: inline;
      font-family: ${fonts.display};
      font-size: 13px;
      text-transform: none;
    }
  }
`;

// Quiet live readout under the ruler: frequency left, hint centred, flow right.
const Readout = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px 48px;
  padding: 12px 0 18px;
  font-family: ${fonts.mono};
  color: ${colors.bark};

  .hint {
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    white-space: nowrap;
    color: ${colors.pebble};
  }

  @media (max-width: 759px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px 24px;

    .hint {
      grid-column: 1 / -1;
      grid-row: 2;
      text-align: center;
    }
  }
`;

const HzBlock = styled.div`
  justify-self: end;
  display: flex;
  align-items: center;
  white-space: nowrap;
  padding: 6px 18px 6px 14px;
  border-radius: 999px;
  font-family: ${fonts.display};
  font-size: 22px;
  letter-spacing: -0.01em;
  color: ${colors.linen};
  ${glow}
  &::before {
    opacity: 1;
  }

  @media (max-width: 759px) {
    justify-self: start;
    font-size: 19px;
  }
`;

const FlowBlock = styled.div`
  justify-self: start;
  width: 200px;

  .top {
    display: flex;
    align-items: baseline;
    gap: 8px;
    white-space: nowrap;
  }
  b {
    min-width: 2.4em;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 24px;
    line-height: 1;
    letter-spacing: -0.02em;
    color: ${colors.ink};
    font-variant-numeric: tabular-nums;
  }
  small {
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }
  .bar {
    height: 3px;
    margin-top: 7px;
    border-radius: 3px;
    background: ${colors.sand};
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    border-radius: 3px;
    transition: width 0.25s linear, background 0.3s ease;
  }

  @media (max-width: 759px) {
    width: 100%;
  }
`;

const LiveDot = styled.i`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 10px;
  background: ${(p) => p.$color};
  box-shadow: 0 0 0 3px ${(p) => p.$color}33;
  transition: background 0.3s ease;
`;

const Hero = () => {
  const scope = useRef(null);
  // Dominant frequency (Hz) reported by the dial.
  const [focus, setFocus] = useState(20);
  const [touch] = useState(() => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches);
  const handleFocus = useCallback((v) => setFocus((prev) => (Math.abs(prev - v) > 0.05 ? v : prev)), []);

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });
      tl.from(".line > span", { yPercent: 110, duration: 1.2, stagger: 0.1 })
        .from("[data-hero-fade]", { y: 24, opacity: 0, duration: 1, stagger: 0.08 }, "-=0.8")
        .from("[data-hero-field]", { opacity: 0, duration: 1.6, ease: "power2.out" }, "-=0.9");
    }, scope);
    return () => ctx.revert();
  }, []);

  const hz = Math.round(focus);
  const band = bandOfHz(focus);
  const tone = rgb(colourForHz(focus));
  const flow = flowScore(focus);

  return (
    <Wrap ref={scope} aria-labelledby="hero-title">
      <Container>
        <Copy>
          <div>
            <Title id="hero-title">
              <span className="line"><span>{hero.titleBrand}</span></span>
              <span className="line"><span>{hero.titleLead}</span></span>
              <span className="line"><span><em>{hero.titleEmphasis}</em></span></span>
              <span className="line"><span>{hero.titleTail}</span></span>
            </Title>
          </div>
          <Side>
            <Lead data-hero-fade>
              {hero.body}
              <span className="close">{hero.bodyClose}</span>
            </Lead>
            <Actions data-hero-fade>
              <PrimaryButton to={links.register}>
                {hero.primaryCta} <Arrow />
              </PrimaryButton>
            </Actions>
            <Award data-hero-fade href={award.href} target="_blank" rel="noopener noreferrer">
              <Laurel />
              <span>
                <b>
                  {award.title} <i>·</i> {award.region} <i>·</i> {award.country}
                </b>
                <small>
                  {award.year} · {award.source} · {award.origin}
                </small>
              </span>
            </Award>
          </Side>
        </Copy>
      </Container>

      <Field data-hero-field>
        <Waves>
          <NeuralField dial dialRange={DIAL_RANGE} onFocus={handleFocus} lines={10} />
          <FieldFade />
          <FlowGuide $on={band === FLOW_ZONE.band} style={{ left: `${FLOW_ZONE.mid}%` }} />
          <StateWords aria-hidden="true" $crowded={band === BANDS[2] || band === BANDS[4]}>
            {ZONES.map((z, i) => {
              const on = z.band === band;
              const last = i === ZONES.length - 1;
              return (
                <span
                  key={z.band.key}
                  className={[i === 0 || last ? "" : "mid", on ? "on" : "", z === FLOW_ZONE ? "flow" : ""].join(" ")}
                  style={{ left: `${z.mid}%`, ...(on ? pill(tone) : {}) }}
                >
                  {z.state}
                </span>
              );
            })}
          </StateWords>
        </Waves>

        <Ruler aria-hidden="true">
          {ZONES.map((z) => {
            const on = z.band === band;
            return (
              <React.Fragment key={z.band.key}>
                <span className="seg" style={{ left: `${z.from}%`, width: `${z.to - z.from}%`, background: on ? tone : undefined }} />
                <span className={on ? "name on" : "name"} style={{ left: `${z.mid}%`, ...(on ? pill(tone, 4) : {}) }}>
                  <span className="full">{z.band.name}</span>
                  <span className="sym">{z.band.symbol}</span>
                </span>
              </React.Fragment>
            );
          })}
          {TICKS.map((t) => {
            const zone = ZONES.find((z) => z.band === band);
            const p = posFor(t);
            const on = zone && p >= zone.from - 0.01 && p <= zone.to + 0.01;
            return (
            <span key={t} className={on ? "tick on" : "tick"} style={{ left: `${p}%`, color: on ? tone : undefined }}>
              <span>
                {t}
                <span className="unit"> Hz</span>
              </span>
            </span>
            );
          })}
          <span className="marker" style={{ left: `${posFor(focus)}%`, background: tone }} />
        </Ruler>

        <Container>
          <Readout aria-hidden="true">
            <HzBlock style={pill(tone)}>
              <LiveDot $color={colors.linen} />
              {hz} Hz · {band.name}
            </HzBlock>
            <p className="hint">↔ {touch ? hero.hintTouch : hero.hintPointer}</p>
            <FlowBlock>
              <div className="top">
                <b>{Math.round(flow * 100)}%</b>
                <small>{hero.flowLabel}</small>
              </div>
              <div className="bar">
                <span style={{ width: `${Math.max(3, flow * 100)}%`, background: tone }} />
              </div>
            </FlowBlock>
          </Readout>
        </Container>
      </Field>
    </Wrap>
  );
};

export default Hero;
